/**
 * LR-493: COB (close-of-business, T-1) FX for the Run-MTM target preview and the repo margin-call
 * "pay in another major" equivalent — display/preview only (the backend writes the authoritative net).
 *
 * Deliberately SEPARATE from `currency-service.ts` (the live-spot feed the trade-entry form uses):
 * the two FX sources must not mix. This one is fed by `GET /api/v1/fx-rates/cob/` (the SAME COB
 * daily-candle close the backend prices and the repo margin call use), restricted to the five
 * payable majors, with NO hardcoded fallback and NO trader override — a missing pair is shown as
 * "no COB", never silently converted.
 *
 * Rates are EUR-based (units of ccy per 1 EUR), so one vector triangulates every pair:
 *   rate(from -> to) = eur[to] / eur[from]
 * The rates live in module state (not a hook) so `cobConvertToTarget` can be called from render
 * without prop-drilling; `useCobFxRatesSync` injects them and bumps the shared fx-rates version so
 * its two consumers re-render when COB lands: the Run-MTM dialog's "Delta ≈" preview (MtmPage) and
 * the repo "Pay ≈" transfer line (PostRepoMarginCallDialog).
 */
import { eurTriangulate } from './eur-triangulation';

/** The currencies a cross-currency MTM/MC figure can be settled/displayed in (product: majors). */
export const COB_FX_MAJORS = ['USD', 'EUR', 'CHF', 'GBP', 'HKD'] as const;

/** Where the rate `cobConvertToTarget` applied came from:
 *  - `'cob'`  — a COB (T-1) rate (or a trivial same-ccy pair);
 *  - `'none'` — no COB rate known for the pair; the amount is left unconverted (rate 1). */
export type CobRateSource = 'cob' | 'none';

export interface CobConversion {
  /** `amount × rate`, in `toCcy` (== amount when source is 'none'). */
  value: number;
  /** The COB rate applied (or 1 for none / same-ccy). */
  rate: number;
  /** Provenance — callers show 'none' as "no COB" rather than a converted figure. */
  source: CobRateSource;
}

// Module-level EUR-based COB rates, injected by useCobFxRatesSync.
let _cobEurRates: Record<string, number> | null = null;

export const setCobRates = (eurRates: Record<string, number> | null): void => {
  _cobEurRates = eurRates;
};

/** The COB rate to convert 1 `from` into `to`, or undefined when either leg has no COB candle.
 *  Uses the shared EUR-triangulation math (review CU4) over THIS module's own COB vector — the COB
 *  cache stays isolated from the live/fallback feed. */
export const getCobRate = (from: string, to: string): number | undefined =>
  eurTriangulate(from, to, _cobEurRates);

/**
 * Convert a native `amount` from `fromCcy` to `toCcy` at the COB rate, for DISPLAY only. An
 * unknown pair (no COB candle for a leg) leaves the amount unconverted (rate 1, source 'none')
 * so the caller can show "no COB" instead of a wrong figure.
 */
export const cobConvertToTarget = (
  amount: number,
  fromCcy: string,
  toCcy: string,
): CobConversion => {
  const rate = getCobRate(fromCcy, toCcy);
  if (rate === undefined) return { value: amount, rate: 1, source: 'none' };
  return { value: amount * rate, rate, source: 'cob' };
};
